/* @jsx h */
import { defineComponent, h, ref, onMounted, computed, watch } from "vue";
import { useRoute } from "vue-router";
import { TableStatus } from "@lemmonhq/types";
import { Loading, MasterDetail } from "@app/vue/components/index";
import { setTitle } from "@lemmonhq/orders";
import { api } from "@app/data/index";
import { ManagerTablesSidebar } from "./components/ManagerTablesSidebar";
import { TableQrModal } from "./components/TableQrModal";

type TableFilter = "all" | "free" | "busy";

export const ManagerTablesPage = defineComponent({
  name: "ManagerTablesPage",
  setup() {
    const tables = ref<TableStatus[]>([]);
    const loading = ref(true);
    const route = useRoute();
    const selectedQrTable = ref<TableStatus | null>(null);

    const filter = computed(() => (route.query.filter as TableFilter) || "all");
    const searchQuery = ref("");

    const fetchTables = async () => {
      try {
        const data = await api.getTablesMeta();
        tables.value = data;
      } catch (error) {
        console.error("Failed to fetch tables:", error);
      } finally {
        loading.value = false;
      }
    };

    const filteredTables = computed(() => {
      let filtered = tables.value;

      if (searchQuery.value) {
        filtered = filtered.filter((table) =>
          table.table.name
            .toLowerCase()
            .includes(searchQuery.value.toLowerCase()),
        );
      }

      if (filter.value !== "all") {
        filtered = filtered.filter((table) =>
          filter.value === "busy" ? table.table.is_busy : !table.table.is_busy,
        );
      }

      return filtered;
    });

    watch(
      () => route.query.filter,
      () => {
        fetchTables();
      },
    );

    onMounted(() => {
      setTitle("Tables");
      fetchTables();
    });

    const renderTableRow = (
      table: TableStatus,
      index: number,
      onEdit: Function,
    ) => {
      return (
        <div class="flex items-center justify-between p-4 border-b ">
          <div class="flex items-center space-x-4">
            <div
              class={`w-3 h-3 rounded-full ${
                table.table.is_busy ? "bg-red-500" : "bg-green-500"
              }`}
            />
            <span class="text-lg">{table.table.name}</span>
          </div>
          <div class="flex items-center space-x-4">
            <button
              onClick={() => (selectedQrTable.value = table)}
              class="btn btn-ghost btn-circle"
              title="View QR Code"
            >
              <span class="material-symbols-outlined">qr_code_2</span>
            </button>
          </div>
        </div>
      );
    };

    return () => (
      <div class="flex h-full p-8">
        <div class="flex flex-1 border  rounded-lg overflow-hidden">
          <ManagerTablesSidebar />
          <div class="flex-1 flex flex-col justify-center items-center overflow-hidden rounded-r-lg">
            {loading.value ? (
              <Loading />
            ) : (
              <MasterDetail
                items={filteredTables.value}
                renderItem={(item, i, onEdit) =>
                  renderTableRow(item as TableStatus, i, onEdit)
                }
                renderEditView={() => <div />}
                title="Tables"
              />
            )}
          </div>

          {selectedQrTable.value && (
            <TableQrModal
              table={selectedQrTable.value}
              onClose={() => (selectedQrTable.value = null)}
            />
          )}
        </div>
      </div>
    );
  },
});
